import { selectImageDataVariant } from './imageDataVariant'
import {
  getInlineAssetFields,
  getInlineAssetId,
  isContentfulAssetFile,
  isContentfulLink,
  isGatsbyAsset,
  isOverlayableText,
  isRecord,
  UnknownRecord,
} from './shared'
import { ContentfulPreviewAsset } from '../../services/contentfulPreviewService'

const resolveAssetId = (field: unknown): string | undefined => {
  if (isContentfulLink(field, 'Asset')) return field.sys.id
  return getInlineAssetId(field)
}

/**
 * Resolves whichever asset shape live preview handed us to the preview asset that describes it.
 *
 * A bare Link carries no file, so it only resolves when the asset is in the preview payload (the
 * page include graph, or lazily fetched by `useContentfulPreviewAssets`). The inline shapes carry
 * the file but never the server-built `imageData`, so they are matched back to the payload by id
 * where possible — an inline shape with no id and no payload entry cannot be swapped.
 */
const resolvePreviewAsset = (
  field: unknown,
  assetsById: Map<string, ContentfulPreviewAsset>,
): ContentfulPreviewAsset | undefined => {
  const id = resolveAssetId(field)
  if (id && assetsById.has(id)) return assetsById.get(id)

  return undefined
}

const readAssetFileUrl = (asset: ContentfulPreviewAsset): string | undefined => {
  const file = isRecord(asset.fields) ? asset.fields.file : undefined
  return isContentfulAssetFile(file) ? file.url : undefined
}

/**
 * Builds a Gatsby-shaped `ContentfulAsset` from scratch, for a live rich text embed that has no
 * build-time reference to patch.
 */
export const buildGatsbyAssetReference = (asset: ContentfulPreviewAsset): UnknownRecord | undefined => {
  const gatsbyImageData = selectImageDataVariant(asset.imageData, undefined)
  if (!gatsbyImageData) return undefined

  const fields = isRecord(asset.fields) ? asset.fields : {}
  const url = readAssetFileUrl(asset)
  if (!url) return undefined

  return {
    __typename: 'ContentfulAsset',
    contentful_id: asset.sys.id,
    title: isOverlayableText(fields.title) ? fields.title : '',
    description: isOverlayableText(fields.description) ? fields.description : '',
    file: { url },
    gatsbyImageData,
  }
}

/**
 * Points an asset field at the image the editor just picked, without waiting for a re-source.
 *
 * The Gatsby object is kept and patched rather than rebuilt, so `__typename` and any queried field
 * this does not understand survive. `gatsbyImageData` is not recomputed here: the server already
 * built it with Gatsby's own image pipeline, and this only picks the candidate whose options match
 * the build-time object (see `imageDataVariant.ts`). PF-1644's first attempt reimplemented that
 * math in the browser, which is the part that drifted.
 *
 * Returns `undefined` (keep build time) whenever the live side is not resolvable to a real image,
 * so a draft asset with nothing uploaded blanks nothing.
 */
export const overlayAsset = (
  value: unknown,
  field: unknown,
  assetsById: Map<string, ContentfulPreviewAsset>,
): unknown | undefined => {
  if (!isGatsbyAsset(value)) return undefined

  const inline = getInlineAssetFields(field)
  const asset = resolvePreviewAsset(field, assetsById)
  if (!asset) return undefined

  const url = readAssetFileUrl(asset)
  if (!url) return undefined

  const buildTimeUrl = isRecord(value.file) && typeof value.file.url === 'string' ? value.file.url : undefined

  // Nothing changed: same asset, same file. Deliberately compares the **file**, not just the id —
  // re-uploading a file keeps the asset id and changes only the URL token, so bailing on a matching
  // id alone silently skipped the most common image edit an editor makes.
  if (asset.sys.id === value.contentful_id && url === buildTimeUrl) return undefined

  // The asset we hold is older than what the editor is looking at: live preview sent a file URL
  // that our copy does not have yet. Only the develop proxy can build `gatsbyImageData`, so keep
  // build-time until `useContentfulPreviewAssets` refetches — rendering the stale image under the
  // new id would be worse than not updating.
  const inlineUrl = inline && isContentfulAssetFile(inline.file) ? inline.file.url : undefined
  if (inlineUrl && inlineUrl !== url) return undefined

  const gatsbyImageData = selectImageDataVariant(asset.imageData, value.gatsbyImageData)
  if (!gatsbyImageData) return undefined

  const fields = isRecord(asset.fields) ? asset.fields : {}
  // Prefer the inline payload's title/description when live preview sent them — those are the
  // editor's unsaved keystrokes, newer than anything the proxy fetched.
  const liveFields = inline || fields

  const next: UnknownRecord = { ...value, gatsbyImageData }

  next.contentful_id = asset.sys.id
  if (isRecord(value.file)) next.file = { ...value.file, url }
  if (isOverlayableText(liveFields.title)) next.title = liveFields.title
  if (isOverlayableText(liveFields.description)) next.description = liveFields.description

  return next
}
